/**
 * CPC Search service client.
 * Wraps /sub-projects/{id}/cpc-search/* endpoints.
 */

import apiClient from './api';

export type CpcJobStatus = 'pending' | 'running' | 'pass1' | 'pass2' | 'done' | 'error';

export type LogEntryKind =
  | 'pnc'            // Pass-1 count-only calibration call
  | 'retrieve'       // Pass-1 abstract retrieval
  | 'score'          // Pass-1 relevance scoring summary
  | 'top_classes'    // Pass-1 top CPC classes after CPCR
  | 'class_retrieve' // Pass-2 abstract retrieval for a class
  | 'class_score';   // Pass-2 per-(class,feature) average score

export interface LogEntry {
  ts: string;                       // HH:MM:SS
  phase: '1' | '2' | '3';
  kind: LogEntryKind;
  feature_name?: string | null;
  cpc_class?: string | null;
  query?: string | null;
  count?: number | null;            // PNC result count
  docs_retrieved?: number | null;
  docs_relevant?: number | null;    // docs with score >= threshold
  top_classes?: string[] | null;
  avg_score?: number | null;
  msg: string;
}

export interface JobStatusResponse {
  job_id: string;
  status: CpcJobStatus;
  progress_message: string;
  pass1_done: number;
  pass1_total: number;
  pass2_done: number;
  pass2_total: number;
  log: LogEntry[];
  error?: string | null;
}

export interface CpcClassEntry {
  cpc_class: string;
  relevance_by_feature: Record<string, number>;
  doc_count?: number | null;
}

export interface CpcTableFeature {
  id: string;
  name: string;
  final_query: string;
}

export interface CpcTable {
  features: CpcTableFeature[];
  classes: CpcClassEntry[];
  generated_at: string;
}

export interface JobHistoryEntry {
  job_id: string;
  status: CpcJobStatus;
  created_at: string;          // ISO datetime
  feature_count: number;
  class_count: number;
  error?: string | null;
}

export const cpcSearchService = {
  async startJob(subProjectId: string): Promise<{ job_id: string; status: CpcJobStatus }> {
    const res = await apiClient.post(`/sub-projects/${subProjectId}/cpc-search/start`);
    return res.data;
  },

  async getStatus(subProjectId: string, jobId: string): Promise<JobStatusResponse> {
    const res = await apiClient.get(`/sub-projects/${subProjectId}/cpc-search/${jobId}/status`);
    return res.data;
  },

  async getResult(subProjectId: string, jobId: string): Promise<CpcTable> {
    const res = await apiClient.get(`/sub-projects/${subProjectId}/cpc-search/${jobId}/result`);
    return res.data;
  },

  async listHistory(subProjectId: string): Promise<JobHistoryEntry[]> {
    const res = await apiClient.get(`/sub-projects/${subProjectId}/cpc-search/history`);
    return res.data;
  },

  /** Returns the most recent completed CPC table, or null if none exists. */
  async getLatest(subProjectId: string): Promise<CpcTable | null> {
    try {
      const res = await apiClient.get(`/sub-projects/${subProjectId}/cpc-search/latest`);
      return res.data;
    } catch (e: any) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },
};
